/**
 * The two integration cards of `/settings/tenant` (#2366): Shopify and Onfleet.
 *
 * Header: the provider, where the live credentials come from (`source`: saved
 * on the tenant / none), when they were verified,
 * and the Pencil that opens the card for editing.
 *
 * Body: one labelled panel row per value (#2429, #2431). The display state
 * shows the account row (`externalId`: Shopify's shop domain, Onfleet's
 * organization id), then one masked row per secret of `fields` (names +
 * set/unset only, #2385) — the row of the field the server took `last4` from
 * shows `••••<last4>`. The edit state builds the rows from the client's
 * field table of the credential kind and puts an input in the value cell of
 * the SAME row (same key), so the rows stay put and there is never a second
 * set of inputs; an account that is not an input (Onfleet) stays a read-only
 * row. Editing replaces the whole credential set: the footer VERIFIES and
 * saves it (`PUT /api/tenant/integrations/{provider}`), or cancels. The
 * live-traffic warning shows only while editing. Unconfigured cards open in the
 * edit state (`initialOpen = source !== 'tenant'`).
 *
 * Secret inputs are password fields with autocomplete off, and the secrets are
 * cleared (and the mutation reset) after a successful save and on cancel;
 * nothing typed is written to the query cache or to storage. The shop domain
 * is not a secret: its input starts from the saved `externalId`.
 *
 * Below, a collapsible block (collapsed by default) lists the provider's webhook
 * subscriptions (#2385) and offers "Register webhooks", which only works for
 * credentials saved on the tenant (the backend answers 409 when unavailable).
 */
import { useId, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, ChevronRight } from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@resource-kit/ui/react';
import { formatDateTime } from '@lib/datetime';
import type {
  IntegrationProvider,
  IntegrationSource,
  ShopifyCredentialKind,
  ShopifyCredentialsBody,
  TenantIntegrationStatus,
} from '@/services/tenants';
import {
  requestErrorMessage,
  useIntegrationWebhooks,
  useRegisterWebhooks,
  useSaveOnfleetIntegration,
  useSaveShopifyIntegration,
} from '../hooks/useTenants';
import { EditButton, SettingsRow } from './SettingsSection';

const EM_DASH = '—';
const MASK = '••••••';

/**
 * The row that shows `externalId`, the provider account (#2431): Shopify's
 * shop domain (also a field of `fields` and an input), Onfleet's organization
 * id (read-only; the server looks it up from the API key).
 */
const ACCOUNT_FIELD = {
  shopify: 'shopDomain',
  onfleet: 'organizationId',
} as const satisfies Record<IntegrationProvider, string>;

/** The field the server takes `last4` from, per credential kind (tenant-routes `rowLast4`). */
const LAST4_FIELD = {
  legacy_token: 'accessToken',
  client_credentials: 'clientSecret',
  api_key: 'apiKey',
} as const satisfies Record<NonNullable<TenantIntegrationStatus['credentialKind']>, string>;

const SOURCE_LABEL_KEY = {
  tenant: 'tenants.integrations.source.tenant',
  none: 'tenants.integrations.source.none',
} as const satisfies Record<IntegrationSource, string>;

const SOURCE_VARIANT = {
  tenant: 'success',
  none: 'outline',
} as const satisfies Record<IntegrationSource, 'success' | 'secondary' | 'outline'>;

const TITLE_KEY = {
  shopify: 'tenants.integrations.shopify.title',
  onfleet: 'tenants.integrations.onfleet.title',
} as const satisfies Record<IntegrationProvider, string>;

const LIVE_WARNING_KEY = {
  shopify: 'tenants.integrations.shopify.liveWarning',
  onfleet: 'tenants.integrations.onfleet.liveWarning',
} as const satisfies Record<IntegrationProvider, string>;

const SHOPIFY_KINDS = [
  { kind: 'legacy_token', labelKey: 'tenants.integrations.shopify.kind.legacy_token' },
  { kind: 'client_credentials', labelKey: 'tenants.integrations.shopify.kind.client_credentials' },
] as const satisfies readonly { kind: ShopifyCredentialKind; labelKey: string }[];

/**
 * Row labels of the credential fields, in both states (#2385, #2429). The
 * display list comes from the backend (`fields`); an unknown name is shown as is.
 */
const FIELD_LABEL_KEY: Partial<Record<string, string>> = {
  shopDomain: 'tenants.integrations.fieldName.shopDomain',
  accessToken: 'tenants.integrations.fieldName.accessToken',
  apiSecret: 'tenants.integrations.fieldName.apiSecret',
  webhookSecret: 'tenants.integrations.fieldName.webhookSecret',
  clientId: 'tenants.integrations.fieldName.clientId',
  clientSecret: 'tenants.integrations.fieldName.clientSecret',
  apiKey: 'tenants.integrations.fieldName.apiKey',
  organizationId: 'tenants.integrations.fieldName.organizationId',
};

/** The props every secret input carries (#2366 invariant 2). */
const SECRET_INPUT = { type: 'password', autoComplete: 'off' } as const;

/** One editable credential field: its name (as in `fields`) and input id. */
interface CredentialField<K extends string> {
  name: K;
  id: string;
  secret: boolean;
}

const EMPTY_SHOPIFY = {
  shopDomain: '',
  accessToken: '',
  apiSecret: '',
  webhookSecret: '',
  clientId: '',
  clientSecret: '',
};

type ShopifyForm = typeof EMPTY_SHOPIFY;
type ShopifyFieldName = keyof ShopifyForm;

const SHOP_DOMAIN_FIELD = { name: 'shopDomain', id: 'shopify-shop-domain', secret: false } as const;

/** The edit-state rows of each Shopify credential kind, in display order. */
const SHOPIFY_FIELDS = {
  legacy_token: [
    SHOP_DOMAIN_FIELD,
    { name: 'accessToken', id: 'shopify-access-token', secret: true },
    { name: 'apiSecret', id: 'shopify-api-secret', secret: true },
    { name: 'webhookSecret', id: 'shopify-webhook-secret', secret: true },
  ],
  client_credentials: [
    SHOP_DOMAIN_FIELD,
    { name: 'clientId', id: 'shopify-client-id', secret: false },
    { name: 'clientSecret', id: 'shopify-client-secret', secret: true },
  ],
} as const satisfies Record<ShopifyCredentialKind, readonly CredentialField<ShopifyFieldName>[]>;

const EMPTY_ONFLEET = { apiKey: '', webhookSecret: '' };

type OnfleetForm = typeof EMPTY_ONFLEET;

const ONFLEET_FIELDS = [
  { name: 'apiKey', id: 'onfleet-api-key', secret: true },
  { name: 'webhookSecret', id: 'onfleet-webhook-secret', secret: true },
] as const satisfies readonly CredentialField<keyof OnfleetForm>[];

/**
 * P1a: the current tenant is hgs, so a save swaps the credentials production
 * uses for live traffic on the spot. Shown only while the card is edited.
 */
function LiveCredentialsWarning({ provider }: { provider: IntegrationProvider }) {
  const { t } = useTranslation();
  return (
    <div
      role="alert"
      data-testid={`integration-live-warning-${provider}`}
      className="flex items-start gap-2 bg-warning/10 px-5 py-3 text-sm text-foreground"
    >
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
      <span>{t(LIVE_WARNING_KEY[provider])}</span>
    </div>
  );
}

/**
 * The rows of a card — ONE list for both states (#2429, #2431). Display: the
 * account row, one masked row per secret field, and the fallback last-4 row.
 * Edit: one input row per field of the kind, in the same row element (same
 * key), labelled by the row label, after the account row if it is no input.
 */
function CredentialRows<K extends string>({
  status,
  editing,
  fields,
  form,
  onChange,
}: {
  status: TenantIntegrationStatus;
  editing: boolean;
  fields: readonly CredentialField<K>[];
  form: Record<K, string>;
  onChange: (name: K, value: string) => void;
}) {
  const { t } = useTranslation();
  const label = (name: string) => t(FIELD_LABEL_KEY[name] ?? name);
  const testId = (name: string) => `integration-field-${status.provider}-${name}`;
  const row = (name: string, value: ReactNode) => (
    <SettingsRow key={name} testId={testId(name)} label={label(name)}>
      {value}
    </SettingsRow>
  );
  const account = ACCOUNT_FIELD[status.provider];
  const accountRow = row(
    account,
    status.externalId ? (
      <span className="font-mono text-xs">{status.externalId}</span>
    ) : (
      <span className="text-muted-foreground">{EM_DASH}</span>
    ),
  );
  const masked = (text: string) => (
    <span className="font-mono text-xs tracking-widest">{text}</span>
  );

  if (editing) {
    const inputs = fields.map((field) => (
      <SettingsRow
        key={field.name}
        testId={testId(field.name)}
        label={label(field.name)}
        labelFor={field.id}
      >
        <Input
          id={field.id}
          className="h-9 max-w-md"
          value={form[field.name]}
          {...(field.secret ? SECRET_INPUT : { autoComplete: 'off' })}
          onChange={(e) => onChange(field.name, e.target.value)}
        />
      </SettingsRow>
    ));
    return fields.some((field) => field.name === account) ? inputs : [accountRow, ...inputs];
  }
  const last4Field = status.credentialKind ? LAST4_FIELD[status.credentialKind] : null;
  const last4 = status.last4 ? `••••${status.last4}` : null;
  const secrets = status.fields.filter((field) => field.name !== account);
  return [
    accountRow,
    ...secrets.map((field) =>
      row(
        field.name,
        field.configured ? (
          masked((field.name === last4Field && last4) || MASK)
        ) : (
          <span className="text-muted-foreground">{t('tenants.integrations.notSet')}</span>
        ),
      ),
    ),
  ];
}

/** The provider's subscriptions under the live credentials (#2385), collapsible. */
function WebhookSubscriptions({ status }: { status: TenantIntegrationStatus }) {
  const { t } = useTranslation();
  const configured = status.source !== 'none';
  const query = useIntegrationWebhooks(status.provider, configured);
  // Held here, not in the body, so the last result survives a collapse.
  const register = useRegisterWebhooks(status.provider);
  const [open, setOpen] = useState(false);
  const bodyId = useId();
  if (!configured) return null;

  let body: ReactNode;
  if (query.isPending) {
    body = (
      <p className="text-sm text-muted-foreground">{t('tenants.integrations.webhooks.loading')}</p>
    );
  } else if (query.isError) {
    body = (
      <p className="text-sm text-destructive">
        {t('tenants.integrations.webhooks.loadFailed', {
          message: requestErrorMessage(query.error),
        })}
      </p>
    );
  } else {
    const { items, missing } = query.data;
    body = (
      <>
        {items.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {t('tenants.integrations.webhooks.empty')}
          </p>
        ) : (
          <div className="overflow-hidden rounded-md border">
            <Table data-testid={`webhooks-table-${status.provider}`}>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('tenants.integrations.webhooks.topic')}</TableHead>
                  <TableHead>{t('tenants.integrations.webhooks.url')}</TableHead>
                  <TableHead>{t('tenants.integrations.webhooks.target')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item, i) => (
                  <TableRow key={`${item.topic}-${item.url ?? ''}-${i}`}>
                    <TableCell className="font-mono text-xs">{item.topic}</TableCell>
                    <TableCell className="font-mono text-xs break-all">
                      {item.url ?? EM_DASH}
                    </TableCell>
                    <TableCell>
                      {item.ours ? (
                        <Badge variant="success">{t('tenants.integrations.webhooks.ours')}</Badge>
                      ) : (
                        <span className="text-muted-foreground">
                          {t('tenants.integrations.webhooks.elsewhere')}
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        {missing.length > 0 && (
          <div className="text-sm" data-testid={`webhooks-missing-${status.provider}`}>
            <span className="text-muted-foreground">
              {t('tenants.integrations.webhooks.missing')}:{' '}
            </span>
            <span className="font-mono text-xs">{missing.join(', ')}</span>
          </div>
        )}
      </>
    );
  }

  return (
    <div className="border-t">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={bodyId}
        onClick={() => setOpen((value) => !value)}
        className="flex w-full items-center gap-2 px-5 py-3 text-left text-sm transition-colors hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <ChevronRight
          className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${open ? 'rotate-90' : ''}`}
        />
        <span className="font-medium">{t('tenants.integrations.webhooks.title')}</span>
        {/* The block starts collapsed, so a failed list must show without expanding it. */}
        {query.isError ? (
          <span className="ml-auto text-xs text-destructive">
            {t('tenants.integrations.webhooks.summaryFailed')}
          </span>
        ) : (
          query.data && (
            <span className="ml-auto text-xs text-muted-foreground">
              {t('tenants.integrations.webhooks.summary', {
                count: query.data.items.length,
                missing: query.data.missing.length,
              })}
            </span>
          )
        )}
      </button>
      {open && (
        <div id={bodyId} className="space-y-3 px-5 pb-4">
          {body}
          {status.provider === 'shopify' && (
            <p className="text-xs text-muted-foreground">
              {t('tenants.integrations.webhooks.shopifyAdminNote')}
            </p>
          )}
          <RegisterWebhooks status={status} mutation={register} />
        </div>
      )}
    </div>
  );
}

function RegisterWebhooks({
  status,
  mutation,
}: {
  status: TenantIntegrationStatus;
  mutation: ReturnType<typeof useRegisterWebhooks>;
}) {
  const { t } = useTranslation();
  const enabled = status.source === 'tenant';
  const result = mutation.data;
  const lists = result
    ? [
        {
          key: 'created',
          labelKey: 'tenants.integrations.webhooks.created',
          items: result.created,
        },
        {
          key: 'existing',
          labelKey: 'tenants.integrations.webhooks.existing',
          items: result.existing,
        },
      ]
    : [];

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-3">
        <Button
          variant="outline"
          size="sm"
          disabled={!enabled || mutation.isPending}
          onClick={() => mutation.mutate()}
        >
          {mutation.isPending
            ? t('tenants.integrations.registering')
            : t('tenants.integrations.registerWebhooks')}
        </Button>
        {!enabled && (
          <span className="text-xs text-muted-foreground">
            {t('tenants.integrations.registerHint')}
          </span>
        )}
      </div>
      {mutation.isError && (
        <p className="text-sm text-destructive">
          {t('tenants.integrations.registerFailed', {
            message: requestErrorMessage(mutation.error),
          })}
        </p>
      )}
      {lists.map((list) => (
        <div
          key={list.key}
          className="text-sm"
          data-testid={`webhooks-${list.key}-${status.provider}`}
        >
          <span className="text-muted-foreground">{t(list.labelKey)}: </span>
          {list.items.length > 0 ? (
            <span className="font-mono text-xs">{list.items.join(', ')}</span>
          ) : (
            t('tenants.integrations.webhooks.none')
          )}
        </div>
      ))}
    </div>
  );
}

function SaveFeedback({
  error,
  invalidKey,
}: {
  error: unknown;
  /** i18n key of the client-side hint that blocked the submit, if any. */
  invalidKey: string | null;
}) {
  const { t } = useTranslation();
  if (invalidKey) {
    return <p className="text-sm text-destructive">{t(invalidKey)}</p>;
  }
  if (error) {
    return (
      <p className="text-sm text-destructive">
        {t('tenants.integrations.saveFailed', { message: requestErrorMessage(error) })}
      </p>
    );
  }
  return null;
}

/** The edit footer: notes and feedback, then "Cancel" and "Verify and save". */
function EditFooter({
  notes,
  feedback,
  pending,
  onSubmit,
  onCancel,
}: {
  notes: readonly string[];
  feedback: ReactNode;
  pending: boolean;
  onSubmit: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-3 border-t bg-muted/40 px-5 py-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="space-y-1">
        {notes.map((key) => (
          <p key={key} className="text-xs text-muted-foreground">
            {t(key)}
          </p>
        ))}
        {feedback}
      </div>
      <div className="flex shrink-0 gap-2">
        <Button variant="outline" size="sm" onClick={onCancel} disabled={pending}>
          {t('tenants.integrations.cancel')}
        </Button>
        <Button size="sm" onClick={onSubmit} disabled={pending}>
          {pending ? t('tenants.integrations.saving') : t('tenants.integrations.save')}
        </Button>
      </div>
    </div>
  );
}

function IntegrationCard({
  status,
  editing,
  saved,
  onToggleEdit,
  rows,
  footer,
}: {
  status: TenantIntegrationStatus;
  editing: boolean;
  saved: boolean;
  onToggleEdit: () => void;
  /** The panel rows: account and credential rows, preceded by any edit-only row. */
  rows: ReactNode;
  footer: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <Card data-testid={`integration-card-${status.provider}`} className="overflow-hidden shadow-xs">
      <div className="flex items-start justify-between gap-4 border-b px-5 py-4">
        <div className="min-w-0 space-y-1.5">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-sm font-semibold">{t(TITLE_KEY[status.provider])}</h3>
            <Badge variant={SOURCE_VARIANT[status.source]}>
              {t(SOURCE_LABEL_KEY[status.source])}
            </Badge>
          </div>
          <p className="text-xs text-muted-foreground">
            {t('tenants.integrations.field.verifiedAt')}{' '}
            <span data-testid={`integration-verified-at-${status.provider}`}>
              {status.verifiedAt ? formatDateTime(status.verifiedAt) : EM_DASH}
            </span>
          </p>
        </div>
        <EditButton
          label={t('tenants.integrations.edit')}
          pressed={editing}
          onClick={onToggleEdit}
        />
      </div>
      {editing && <LiveCredentialsWarning provider={status.provider} />}
      <div className="divide-y">{rows}</div>
      {editing && footer}
      {!editing && saved && (
        <p className="border-t px-5 py-2 text-sm text-success">{t('tenants.integrations.saved')}</p>
      )}
      <WebhookSubscriptions status={status} />
    </Card>
  );
}

const REQUIRED_KEY = 'tenants.integrations.required';
const SHOPIFY_SECRET_REQUIRED_KEY = 'tenants.integrations.shopify.secretRequired';
const EDIT_NOTE_KEY = 'tenants.integrations.editNote';
const SHOPIFY_SECRET_HINT_KEY = 'tenants.integrations.shopify.secretHint';

/**
 * Client-side hint only — the #2365 zod schema is authoritative. A legacy token
 * needs at least one of the API secret and the webhook secret (the backend
 * refine: webhooks cannot be verified without one).
 */
function shopifyHintKey(kind: ShopifyCredentialKind, form: ShopifyForm): string | null {
  if (shopifyBody(kind, form) === null) return REQUIRED_KEY;
  if (kind === 'legacy_token' && !form.apiSecret.trim() && !form.webhookSecret.trim()) {
    return SHOPIFY_SECRET_REQUIRED_KEY;
  }
  return null;
}

/** Build the #2365 request body; `null` when a required field is empty. */
function shopifyBody(
  kind: ShopifyCredentialKind,
  form: ShopifyForm,
): ShopifyCredentialsBody | null {
  const shopDomain = form.shopDomain.trim();
  if (kind === 'client_credentials') {
    const clientId = form.clientId.trim();
    const clientSecret = form.clientSecret.trim();
    if (!shopDomain || !clientId || !clientSecret) return null;
    return { credentialKind: kind, shopDomain, clientId, clientSecret };
  }
  const accessToken = form.accessToken.trim();
  if (!shopDomain || !accessToken) return null;
  const apiSecret = form.apiSecret.trim();
  const webhookSecret = form.webhookSecret.trim();
  // Optional secrets are omitted, never sent as "" (the backend rejects "").
  return {
    credentialKind: kind,
    shopDomain,
    accessToken,
    ...(apiSecret ? { apiSecret } : {}),
    ...(webhookSecret ? { webhookSecret } : {}),
  };
}

/**
 * The display / edit toggle of a card. `reset` returns the form to its initial
 * state; it runs on every toggle and on close, so a reopened form never holds
 * a typed secret.
 */
function useEditToggle(initialOpen: boolean, reset: () => void) {
  const [editing, setEditing] = useState(initialOpen);
  const close = () => {
    setEditing(false);
    reset();
  };
  const toggle = () => {
    setEditing((open) => !open);
    reset();
  };
  return { editing, toggle, close };
}

export function ShopifyIntegrationCard({ status }: { status: TenantIntegrationStatus }) {
  const { t } = useTranslation();
  const mutation = useSaveShopifyIntegration();
  const [kind, setKind] = useState<ShopifyCredentialKind>(
    status.credentialKind === 'client_credentials' ? 'client_credentials' : 'legacy_token',
  );
  // The shop domain is not a secret: start from the saved one (#2431).
  const initialForm: ShopifyForm = { ...EMPTY_SHOPIFY, shopDomain: status.externalId ?? '' };
  const [form, setForm] = useState<ShopifyForm>(initialForm);
  const [invalidKey, setInvalidKey] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const { editing, toggle, close } = useEditToggle(status.source !== 'tenant', () => {
    setForm(initialForm);
    setInvalidKey(null);
    setSaved(false);
    // Detach from a finished mutation so `gcTime: 0` can drop its body (the
    // secrets) from the MutationCache right away.
    mutation.reset();
  });

  const submit = () => {
    const hintKey = shopifyHintKey(kind, form);
    const body = shopifyBody(kind, form);
    setInvalidKey(hintKey);
    setSaved(false);
    if (hintKey !== null || body === null) return;
    mutation.mutate(body, {
      onSuccess: () => {
        close();
        setSaved(true);
      },
    });
  };

  const kindRow = (
    <SettingsRow label={t('tenants.integrations.shopify.kind.label')}>
      <div
        role="radiogroup"
        aria-label={t('tenants.integrations.shopify.kind.label')}
        className="flex flex-wrap gap-x-5 gap-y-2"
      >
        {SHOPIFY_KINDS.map((option) => (
          <label
            key={option.kind}
            htmlFor={`shopify-kind-${option.kind}`}
            className="flex cursor-pointer items-center gap-2"
          >
            <input
              type="radio"
              id={`shopify-kind-${option.kind}`}
              name="shopify-credential-kind"
              value={option.kind}
              checked={kind === option.kind}
              onChange={() => setKind(option.kind)}
              className="h-4 w-4 accent-primary"
            />
            {t(option.labelKey)}
          </label>
        ))}
      </div>
    </SettingsRow>
  );

  return (
    <IntegrationCard
      status={status}
      editing={editing}
      saved={saved}
      onToggleEdit={toggle}
      rows={
        <>
          {editing && kindRow}
          <CredentialRows
            status={status}
            editing={editing}
            fields={SHOPIFY_FIELDS[kind]}
            form={form}
            onChange={(name, value) => setForm((prev) => ({ ...prev, [name]: value }))}
          />
        </>
      }
      footer={
        <EditFooter
          notes={
            kind === 'legacy_token' ? [EDIT_NOTE_KEY, SHOPIFY_SECRET_HINT_KEY] : [EDIT_NOTE_KEY]
          }
          feedback={<SaveFeedback error={mutation.error} invalidKey={invalidKey} />}
          pending={mutation.isPending}
          onSubmit={submit}
          onCancel={close}
        />
      }
    />
  );
}

export function OnfleetIntegrationCard({ status }: { status: TenantIntegrationStatus }) {
  const mutation = useSaveOnfleetIntegration();
  const [form, setForm] = useState<OnfleetForm>(EMPTY_ONFLEET);
  const [invalidKey, setInvalidKey] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const { editing, toggle, close } = useEditToggle(status.source !== 'tenant', () => {
    setForm(EMPTY_ONFLEET);
    setInvalidKey(null);
    setSaved(false);
    // See the Shopify card: lets `gcTime: 0` drop the secrets.
    mutation.reset();
  });

  const submit = () => {
    const apiKey = form.apiKey.trim();
    const webhookSecret = form.webhookSecret.trim();
    const ok = Boolean(apiKey && webhookSecret);
    setInvalidKey(ok ? null : REQUIRED_KEY);
    setSaved(false);
    if (!ok) return;
    mutation.mutate(
      { apiKey, webhookSecret },
      {
        onSuccess: () => {
          close();
          setSaved(true);
        },
      },
    );
  };

  return (
    <IntegrationCard
      status={status}
      editing={editing}
      saved={saved}
      onToggleEdit={toggle}
      rows={
        <CredentialRows
          status={status}
          editing={editing}
          fields={ONFLEET_FIELDS}
          form={form}
          onChange={(name, value) => setForm((prev) => ({ ...prev, [name]: value }))}
        />
      }
      footer={
        <EditFooter
          notes={[EDIT_NOTE_KEY]}
          feedback={<SaveFeedback error={mutation.error} invalidKey={invalidKey} />}
          pending={mutation.isPending}
          onSubmit={submit}
          onCancel={close}
        />
      }
    />
  );
}
